import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';
import { QuaddieLeg, QuaddieRow } from '../../queries/predictedExotics';
import {
  formatOutlay,
  getMatchBadgeInfo,
  quaddieCombinations,
  quaddieCorrectCount,
} from './predicted-exotics-helpers';

function LegChip({ leg }: { leg: QuaddieLeg }) {
  const tone =
    leg.won === true
      ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-400'
      : leg.won === false
        ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400'
        : 'border-border bg-muted/40 text-muted-foreground';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs ${tone}`}>
      <span className="font-semibold">R{leg.race_number}</span>
      <span className="font-mono">{leg.horses.join(',')}</span>
    </span>
  );
}

const columns: DataTableColumn<QuaddieRow>[] = [
  {
    key: 'date',
    header: 'Date',
    priority: true,
    render: (row) => <span className="font-medium tabular-nums">{row.meeting_date}</span>,
  },
  { key: 'track', header: 'Track', priority: true, render: (row) => row.track_name },
  {
    key: 'type',
    header: 'Quaddie',
    priority: true,
    render: (row) => (
      <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs font-medium">
        {row.quaddie_type === 'early' ? 'Early' : 'Main'}
      </span>
    ),
  },
  {
    key: 'result',
    header: 'Result',
    priority: true,
    render: (row) => {
      const { label, colourClass } = getMatchBadgeInfo(quaddieCorrectCount(row), 4);
      return (
        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold text-white ${colourClass}`}>
          {label === '✓ All 4 Correct' ? '✓ Quaddie' : label}
        </span>
      );
    },
  },
  {
    key: 'combinations',
    header: 'Combos',
    priority: false,
    render: (row) => <span className="tabular-nums">{quaddieCombinations(row).toLocaleString()}</span>,
  },
  {
    key: 'cost',
    header: 'Cost (100%)',
    priority: false,
    render: (row) => <span className="tabular-nums">{formatOutlay(quaddieCombinations(row))}</span>,
  },
  {
    key: 'legs',
    header: 'Legs',
    priority: false,
    render: (row) => (
      <div className="flex flex-wrap gap-1.5">
        {row.legs.map((leg) => (
          <LegChip key={leg.race_id} leg={leg} />
        ))}
      </div>
    ),
  },
];

export function QuaddieTable({ rows }: { rows: QuaddieRow[] }) {
  return (
    <DataTable
      columns={columns}
      data={rows}
      getRowKey={(row) => `${row.meeting_id}-${row.quaddie_type}`}
      emptyMessage="No quaddies with selections in every leg for this date range."
    />
  );
}
